import { useModel as _useModel, mergeModels as _mergeModels, defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, vModelText as _vModelText, withDirectives as _withDirectives, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, createBlock as _createBlock, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "tw-space-y-4" }
const _hoisted_2 = {
  key: 0,
  class: "tw-text-sm tw-text-rose-700"
}
const _hoisted_3 = { class: "tw-mt-5 tw-flex tw-justify-end tw-gap-2 tw-border-t tw-border-slate-200/70 tw-pt-4" }
const _hoisted_4 = ["onClick"]
const _hoisted_5 = ["disabled"]

import { computed, reactive, watch } from 'vue';
import { useApolloClient } from '@vue/apollo-composable';

import Modal from '@/components/core/Modal/Modal.vue';
import CreateVesselMutation from '@/gql/orders/mutations/CreateVessel.gql';
import UpdateVesselMutation from '@/gql/orders/mutations/UpdateVessel.gql';
import VesselsQuery from '@/gql/orders/Vessels.gql';


export default /*@__PURE__*/_defineComponent({
  __name: 'EditVessel',
  props: /*@__PURE__*/_mergeModels({
    vessel: {}
  }, {
    "isOpen": { type: Boolean, ...{ default: false } },
    "isOpenModifiers": {},
  }),
  emits: /*@__PURE__*/_mergeModels(["close", "saved"], ["update:isOpen"]),
  setup(__props: any, { emit: __emit }) {

const props = __props;

const isOpenModel = _useModel<boolean>(__props, 'isOpen');

const emit = __emit;

const apollo = useApolloClient();
const form = reactive({
  name: '',
  mssi: '',
});
const state = reactive({
  loading: false,
  errorMessage: '',
});

const isUpdateMode = computed(() => !!props.vessel?.id);

watch(
  () => [isOpenModel.value, props.vessel],
  () => {
    if (!isOpenModel.value) return;
    form.name = props.vessel?.name || '';
    form.mssi = props.vessel?.mssi || '';
    state.errorMessage = '';
  },
  { immediate: true, deep: true },
);

function close() {
  emit('close');
}

async function save() {
  const normalizedName = form.name.trim();
  if (!normalizedName) {
    state.errorMessage = 'Vessel name is required.';
    return;
  }

  state.loading = true;
  state.errorMessage = '';
  try {
    if (isUpdateMode.value) {
      const response = await apollo.client.mutate({
        mutation: UpdateVesselMutation,
        variables: {
          id: Number(props.vessel?.id),
          name: normalizedName,
          mssi: form.mssi.trim() || null,
        },
        update: (cache, { data }) => {
          // upon completion of the Update, update apollo cache
          const updated = data?.updateVessel?.vessel;
          if (!updated) return;

          // Invalidate paginated vessels cache (all variable variants), so the updated item will be found
          // not worth searching through each cache-pagination-search-query combination to update
          cache.evict({ fieldName: 'vesselsPaginated' });
          cache.evict({ fieldName: 'vesselsCount' });

          // Replace the updated vessel in the unpaginated `vessels` cache
          try {
            const existing: any = cache.readQuery({ query: VesselsQuery });
            if (existing?.vessels) {
              const nextVessels = existing.vessels.map((v: any) =>
                Number(v?.id) === Number(updated.id) ? { ...v, ...updated } : v,
              );
              cache.writeQuery({
                query: VesselsQuery,
                data: { vessels: nextVessels },
              });
            }
          } catch {
            // `vessels` query not in cache yet; nothing to update
          }

          cache.gc();
        },
      });
      emit('saved', response?.data?.updateVessel?.vessel);
    } else {
      const response = await apollo.client.mutate({
        mutation: CreateVesselMutation,
        variables: {
          name: normalizedName,
          mssi: form.mssi.trim() || null,
        },
        update: (cache, { data }) => {
          const created = data?.createVessel?.vessel;
          if (!created) return;

          // Invalidate paginated vessels cache (all variable variants), so the added (created) item will be found
          // not worth trying to add the item in the right place in each cache-pagination-search-query combination
          cache.evict({ fieldName: 'vesselsPaginated' });
          cache.evict({ fieldName: 'vesselsCount' });

          try {
            const existing: any = cache.readQuery({ query: VesselsQuery });

            // add to existing vessels cache if not already present
            if (existing?.vessels) {
              const alreadyPresent = existing.vessels.some(
                (v: any) => Number(v?.id) === Number(created.id),
              );
              if (!alreadyPresent) {
                cache.writeQuery({
                  query: VesselsQuery,
                  data: { vessels: [...existing.vessels, created] },
                });
              }
            } else {
            }
          } catch (e) {
            // `vessels` query not in cache yet; nothing to update
          }

          cache.gc();
        },
      });
      emit('saved', response?.data?.createVessel?.vessel);
    }
    isOpenModel.value = false;
  } catch (error: any) {
    state.errorMessage = error?.message || 'Failed to save vessel.';
  } finally {
    state.loading = false;
  }
}

const loading = computed(() => state.loading);
const errorMessage = computed(() => state.errorMessage);

return (_ctx: any,_cache: any) => {
  const _component_font_awesome_icon = _resolveComponent("font-awesome-icon")!

  return (_openBlock(), _createBlock(Modal, {
    isOpen: isOpenModel.value,
    "onUpdate:isOpen": _cache[2] || (_cache[2] = ($event: any) => ((isOpenModel).value = $event)),
    modalId: "edit-vessel-modal",
    title: isUpdateMode.value ? 'Edit Vessel' : 'Add Vessel',
    maxWidth: "xl",
    onClose: close
  }, {
    content: _withCtx(({ close: closeModal }) => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("div", null, [
          _cache[3] || (_cache[3] = _createElementVNode("label", { class: "tw-block tw-text-sm tw-font-medium tw-text-slate-700" }, "Vessel Name *", -1)),
          _withDirectives(_createElementVNode("input", {
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((form.name) = $event)),
            type: "text",
            class: "tw-mt-1 tw-w-full tw-rounded-lg tw-border tw-border-slate-200/70 tw-bg-white tw-px-3 tw-py-2 tw-text-sm tw-text-slate-900 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-indigo-500/40"
          }, null, 512), [
            [_vModelText, form.name]
          ])
        ]),
        _createElementVNode("div", null, [
          _cache[4] || (_cache[4] = _createElementVNode("label", { class: "tw-block tw-text-sm tw-font-medium tw-text-slate-700" }, "MMSI", -1)),
          _withDirectives(_createElementVNode("input", {
            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((form.mssi) = $event)),
            type: "text",
            class: "tw-mt-1 tw-w-full tw-rounded-lg tw-border tw-border-slate-200/70 tw-bg-white tw-px-3 tw-py-2 tw-text-sm tw-text-slate-900 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-indigo-500/40"
          }, null, 512), [
            [_vModelText, form.mssi]
          ])
        ]),
        (errorMessage.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_2, _toDisplayString(errorMessage.value), 1))
          : _createCommentVNode("", true)
      ]),
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("button", {
          type: "button",
          class: "tw-inline-flex tw-items-center tw-rounded-xl tw-border tw-border-slate-200 tw-bg-white tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-text-slate-700 hover:tw-bg-slate-50",
          onClick: closeModal
        }, " Cancel ", 8, _hoisted_4),
        _createElementVNode("button", {
          type: "button",
          class: "tw-inline-flex tw-items-center tw-gap-2 tw-rounded-xl tw-border tw-border-transparent tw-bg-slate-900 tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-text-white hover:tw-bg-slate-800 disabled:tw-opacity-60",
          disabled: loading.value,
          onClick: save
        }, [
          (loading.value)
            ? (_openBlock(), _createBlock(_component_font_awesome_icon, {
                key: 0,
                icon: "circle-notch",
                spin: "",
                class: "tw-h-4 tw-w-4"
              }))
            : _createCommentVNode("", true),
          _createElementVNode("span", null, _toDisplayString(isUpdateMode.value ? 'Save' : 'Add Vessel'), 1)
        ], 8, _hoisted_5)
      ])
    ]),
    _: 1
  }, 8, ["isOpen", "title"]))
}
}

})